<script setup lang="ts">
import AdministrationSidebarItem from '@/components/sidebar/AdministrationSidebarItem.vue'
import ArticlesSidebarItem from '@/components/sidebar/ArticlesSidebarItem.vue'
import CPanelSidebarItem from '@/components/sidebar/CPanelSidebarItem.vue'
import CloudflareSidebarItem from '@/components/sidebar/CloudflareSidebarItem.vue'
import CompaniesSidebarItem from '@/components/sidebar/CompaniesSidebarItem.vue'
import DashboardSidebarItem from '@/components/sidebar/DashboardSidebarItem.vue'
import DomainsSidebarItem from '@/components/sidebar/DomainsSidebarItem.vue'
import FinanceSidebarItem from '@/components/sidebar/FinanceSidebarItem.vue'
import KamailioSidebarItem from '@/components/sidebar/KamailioSidebarItem.vue'
import LMailSidebarItem from '@/components/sidebar/LMailSidebarItem.vue'
import LinksSidebarItem from '@/components/sidebar/LinksSidebarItem.vue'
import PBXWareSidebarItem from '@/components/sidebar/PBXWareSidebarItem.vue'
import RadiusSidebarItem from '@/components/sidebar/RadiusSidebarItem.vue'
import ReportsSidebarItem from '@/components/sidebar/ReportsSidebarItem.vue'
import SMSGlobalSidebarItem from '@/components/sidebar/SMSGlobalSidebarItem.vue'
import SalesSidebarItem from '@/components/sidebar/SalesSidebarItem.vue'
import SystemSidebarItem from '@/components/sidebar/SystemSidebarItem.vue'
import TPPSidebarItem from '@/components/sidebar/TPPSidebarItem.vue'
import TicketsSidebarItem from '@/components/sidebar/TicketsSidebarItem.vue'
import ToolsSidebarItem from '@/components/sidebar/ToolsSidebarItem.vue'
import VocusMobileSidebarItem from '@/components/sidebar/VocusMobileSidebarItem.vue'
import VocusNBNSidebarItem from '@/components/sidebar/VocusNBNSidebarItem.vue'
import { Accordion } from '@/components/ui/accordion'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { useSidebarStore } from '@/stores/sidebar'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { onMounted, ref, watch } from 'vue'
import { useRoute } from '@/composables/route'
import { TicketRouteName } from '@/modules/ticket/routes'
import UserFeedback from '../UserFeedback.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Button from '../ui/button/Button.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'

const isProduction = import.meta.env.PROD
const version = __APP_VERSION__
const sidebarStore = useSidebarStore()
const { open } = storeToRefs(sidebarStore)
const user = useUserStore()
const activeItem = ref('')
const route = useRoute()
const ticketRoutes = ref([
    TicketRouteName.Index,
    TicketRouteName.Mine,
    TicketRouteName.Triage,
    TicketRouteName.Details,
])
const clearingCache = ref(false)
const hasCache = ref(false)

// const { updateServiceWorker, needRefresh } = useRegisterSW({
//     immediate: true,
//     onRegisteredSW(swUrl, r) {
//         r &&
//             setInterval(async () => {
//                 await r.update()
//             }, 1000)
//     },
//     onNeedRefresh() {
//         toast.info('New update available', {
//             description: 'Click refresh button to update.',
//             action: {
//                 label: 'Refresh',
//                 onClick: () => {
//                     updateServiceWorker()
//                 },
//             },
//             duration: 3600000,
//             position: 'bottom-right',
//         })
//     },
// })

watch(route.name, () => {
    if (ticketRoutes.value.includes(route.name.value)) {
        activeItem.value = 'tickets'
        return
    }
})

onMounted(() => {
    if ('caches' in window) {
        caches.keys().then((names) => {
            hasCache.value = names.length > 0
        })
    }
})

const clearCache = () => {
    if ('caches' in window) {
        clearingCache.value = true
        caches.keys().then((names) => {
            names.forEach((name) => {
                caches.delete(name)
            })

            clearingCache.value = false
            window.location.href = window.location.href
        })
    }
}
</script>

<template>
    <Transition name="slide-horizontal">
        <Accordion
            v-show="open"
            v-model="activeItem"
            type="single"
            collapsible
            class="sticky top-[63px] bg-neutral-700 w-60 self-start"
        >
            <ScrollArea class="h-[calc(100vh-192px)]">
                <DashboardSidebarItem />
                <CompaniesSidebarItem />
                <TicketsSidebarItem />
                <SalesSidebarItem />
                <FinanceSidebarItem />
                <ArticlesSidebarItem :active="activeItem === 'articles'" />
                <ReportsSidebarItem />
                <LinksSidebarItem :active="activeItem === 'links'" />
                <ToolsSidebarItem />
                <DomainsSidebarItem />
                <Separator class="bg-neutral-600 my-4" />
                <h4 class="ml-2.5 px-2 text-xs font-medium text-white mb-2">Providers</h4>
                <CloudflareSidebarItem :active="activeItem === 'cloudflare'" />
                <CPanelSidebarItem :active="activeItem === 'cpanel'" />
                <KamailioSidebarItem
                    v-if="user.hasPermission('kamailio:server:view-any')"
                    :active="activeItem === 'kamailio'"
                />
                <LMailSidebarItem :active="activeItem === 'lmail'" />
                <PBXWareSidebarItem :active="activeItem === 'pbxware'" />
                <RadiusSidebarItem :active="activeItem === 'radius'" />
                <SMSGlobalSidebarItem :active="activeItem === 'smsGlobal'" />
                <TPPSidebarItem :active="activeItem === 'tpp'" />
                <VocusMobileSidebarItem
                    v-if="user.hasPermission('vocus:mobile:server:view-any')"
                    :active="activeItem === 'vocusMobile'"
                />
                <VocusNBNSidebarItem :active="activeItem === 'vocusNBN'" />
            </ScrollArea>
            <Separator class="bg-neutral-600" />
            <SystemSidebarItem />
            <AdministrationSidebarItem />
            <div class="h-12 text-xs px-4 py-3 text-white flex items-center justify-evenly">
                <span>Version: {{ version }}</span>
                <TooltipProvider v-if="hasCache">
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <Button
                                variant="icon"
                                size="icon-sm"
                                :disabled="clearingCache"
                                @click="clearCache"
                            >
                                <FontAwesomeIcon
                                    icon="fal fa-refresh"
                                    size="sm"
                                    :class="{
                                        'animate-spin': clearingCache,
                                    }"
                                />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Update</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <UserFeedback v-if="isProduction" />
            </div>
        </Accordion>
    </Transition>
</template>
